/**
 * Every event the permanent record can hold, and the shape of its payload.
 *
 * The record is the only source of truth: machine state, budgets and track records are all worked out
 * from these events. A payload that was written wrong can never be corrected, because the record is
 * append only, so every event is checked against its schema before it is written.
 *
 * Payloads refuse unknown fields on purpose. A typo in a field name would otherwise be recorded
 * forever and silently ignored by every reader.
 */

import { z } from "zod";

/** An id, as used everywhere in Maschina: a v7 UUID, so ids sort by when they were made. */
const id = z
	.string()
	.regex(/^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/, "not a v7 id");

/** A token amount in its smallest unit, as a string, since JSON numbers can't hold them exactly. */
const amount = z.string().regex(/^\d+$/, "not a whole number of base units");

/** A Solana address or transaction signature, checked properly when it reaches the chain. */
const base58 = z.string().regex(/^[1-9A-HJ-NP-Za-km-z]+$/, "not base58");
const address = base58.min(32).max(44);
const signature = base58.min(64).max(88);

const object = <T extends z.ZodRawShape>(shape: T) => z.strictObject(shape);

const RUN_PAYLOADS = {
	"run.queued": object({
		runId: id,
		scheduledFor: z.iso.datetime(),
		/** Identifies the occurrence, so the same scheduled run can never be queued twice. */
		occurrenceKey: z.string().min(1),
	}),
	"run.started": object({ runId: id, nodeId: id }),
	"run.skipped": object({
		runId: id,
		reason: z.enum([
			"machine_paused",
			"machine_stopped",
			"budget_exhausted",
			"missed_window",
			"provider_unavailable",
			"other",
		]),
		detail: z.string().max(500).optional(),
	}),
	"run.finished": object({
		runId: id,
		outcome: z.enum(["completed", "failed"]),
		durationMs: z.int().nonnegative(),
	}),
};

const TRADE_PAYLOADS = {
	"trade.intended": object({
		runId: id,
		tradeId: id,
		inputMint: address,
		outputMint: address,
		inputAmount: amount,
		quotedOutputAmount: amount,
		slippageBps: z.int().min(0).max(10_000),
		/**
		 * Held back for the network fee on top of the amount being spent. A trade costs what it spends
		 * plus what it costs to send, and a budget that only counts the first slowly drifts.
		 */
		feeAllowance: amount.optional(),
	}),
	"trade.refused": object({
		runId: id,
		tradeId: id,
		/** Maschina's own rules, or the wallet provider's policy. Both refuse independently. */
		by: z.enum(["maschina", "provider"]),
		rule: z.string().min(1).max(100),
		reason: z.string().min(1).max(500),
	}),
	/**
	 * Signed, and about to be sent. Written before the transaction reaches the chain, so a crash in
	 * between leaves a signature to ask the chain about rather than a question nobody can answer.
	 */
	"trade.submitted": object({
		runId: id,
		tradeId: id,
		signature,
		/** After this height the transaction can never land, which is how "it failed" becomes provable. */
		lastValidBlockHeight: amount,
	}),
	/**
	 * What a paper machine would have done. It is written instead of a signature, never beside one: a
	 * machine is either spending money or it is not, and the record should never blur the two.
	 */
	"trade.simulated": object({
		runId: id,
		tradeId: id,
		inputMint: address,
		outputMint: address,
		inputAmount: amount,
		quotedOutputAmount: amount,
		/** What sending it would have cost, so paper and real arithmetic stay comparable. */
		feeAllowance: amount.optional(),
	}),
	"trade.completed": object({
		runId: id,
		tradeId: id,
		signature,
		inputAmount: amount,
		outputAmount: amount,
		feeLamports: amount,
	}),
	"trade.failed": object({
		runId: id,
		tradeId: id,
		stage: z.enum(["quote", "sign", "submit", "confirm"]),
		reason: z.string().min(1).max(500),
		signature: signature.optional(),
	}),
};

const MACHINE_PAYLOADS = {
	"machine.created": object({
		ownerId: id,
		/** The content-addressed definition version the machine is pinned to. */
		definitionVersionId: z.string().regex(/^[0-9a-f]{64}$/, "not a definition version"),
		walletAddress: address,
		/** Where the machine's profit is swept. Absent for a machine made before vaults existed. */
		vaultAddress: address.optional(),
	}),
	"machine.started": object({}),
	"machine.paused": object({
		reason: z.enum([
			"owner",
			"repeated_failures",
			"budget_exhausted",
			"provider_unavailable",
			"other",
		]),
		detail: z.string().max(500).optional(),
	}),
	"machine.resumed": object({}),
	"machine.stopped": object({
		by: z.enum(["owner", "system"]),
		reason: z.string().max(500).optional(),
	}),
	/**
	 * A following machine's band moved: the price it works around is now this one.
	 *
	 * Written when it starts, when the price rises away while it holds what it spends, and when it comes
	 * back from the floor. Every other move of the band follows from a trade, which the record already
	 * has, so this is the one fact the band cannot be rebuilt without.
	 */
	"machine.recentred": object({
		/** The price the band now sits around, in micro-dollars. */
		price: amount,
		because: z.enum(["started", "followed", "after_floor"]),
	}),
	/**
	 * The owner changed the machine's recipe: what kind it is, or how it is set. Everything it did before
	 * stays in the record, and it carries on from there under the new one.
	 */
	"machine.retuned": object({
		from: z.string().regex(/^[0-9a-f]{64}$/, "not a definition version"),
		to: z.string().regex(/^[0-9a-f]{64}$/, "not a definition version"),
	}),
	"machine.limits_changed": object({
		limit: z.enum([
			"maxPerTrade",
			"maxPerDay",
			"budgetGranted",
			"approvedMints",
			"recipients",
			"maxSlippageBps",
			"maxPriceImpactBps",
		]),
		/** The old and new values as text, so every limit is recorded the same way. */
		from: z.string().max(500).nullable(),
		to: z.string().max(500),
	}),
};

const AUTHORITY_PAYLOADS = {
	"authority.used": object({
		action: z.enum(["sign_transaction", "spend_budget", "change_limits", "withdraw"]),
		runId: id.optional(),
		amount: amount.optional(),
	}),
	"authority.denied": object({
		action: z.enum(["sign_transaction", "spend_budget", "change_limits", "withdraw"]),
		rule: z.string().min(1).max(100),
		reason: z.string().min(1).max(500),
	}),
};

/**
 * Returning a machine's funds to its owner.
 *
 * Its own group, not a kind of trade. A trade is a machine doing its job and a withdrawal is an owner
 * taking their money back, and the two must never be counted together: a withdrawal is not spending, it
 * does not touch the budget, and it is the one movement whose destination an owner chose.
 */
/**
 * Tokens that went home in a withdrawal, and which of the machine's accounts each came out of.
 *
 * Absent from a withdrawal of SOL alone, which is every withdrawal written before tokens could be
 * withdrawn. The account is named rather than addressed because what matters later is whether the money
 * left the trading account or the vault, and the record already knows both addresses.
 */
const withdrawnTokens = z
	.array(object({ mint: address, amount, from: z.enum(["trading", "vault"]) }))
	.min(1)
	.max(20);

const WITHDRAWAL_PAYLOADS = {
	"withdrawal.requested": object({
		withdrawalId: id,
		/** The owner's wallet. The only place a machine's funds may ever go. */
		to: address,
		lamports: amount,
		tokens: withdrawnTokens.optional(),
	}),
	/**
	 * The signature, written down before the transaction is sent.
	 *
	 * The same guarantee a trade gets: a crash between signing and sending leaves a name to ask the chain
	 * about, rather than a question nobody can answer. Without it a withdrawal could be signed twice and
	 * pay an owner twice out of a machine that only holds it once.
	 */
	"withdrawal.submitted": object({
		withdrawalId: id,
		signature,
		lastValidBlockHeight: amount,
	}),
	"withdrawal.completed": object({
		withdrawalId: id,
		to: address,
		lamports: amount,
		signature,
		/** What it cost to send, which the owner paid out of the machine's wallet. */
		feeLamports: amount,
		slot: amount,
		tokens: withdrawnTokens.optional(),
	}),
	"withdrawal.failed": object({
		withdrawalId: id,
		reason: z.string().min(1).max(500),
		/** Present when it was signed and sent, and failed after that. */
		signature: signature.optional(),
	}),
};

/**
 * Moving a machine's profit out of the account that trades.
 *
 * Its own group, because a sweep is neither a trade nor a withdrawal. A trade is the machine doing its
 * job, a withdrawal is an owner taking their money out of the system, and a sweep is profit moving from
 * the machine's trading account into the vault beside it, which cannot trade at all. The owner is not
 * asked and nothing leaves their control, so counting it as either of the other two would make both
 * numbers wrong.
 *
 * The amount carries a mint, unlike a withdrawal, which is lamports because SOL is the only thing it has
 * ever moved. Profit is in whatever the machine spends.
 */
const SWEEP_PAYLOADS = {
	/**
	 * The decision, with the numbers it was made from.
	 *
	 * `value` and `floatTarget` are recorded because an owner reading this later is not asking what was
	 * swept, which the amount says. They are asking why, and the answer is the machine was worth this
	 * much against a line of that much.
	 */
	"sweep.requested": object({
		sweepId: id,
		/** The vault beside the machine's trading account. The only place a sweep may go. */
		to: address,
		mint: address,
		amount,
		value: amount,
		floatTarget: amount,
	}),
	/**
	 * The signature, written down before the transaction is sent.
	 *
	 * The same guarantee a trade and a withdrawal get: a crash between signing and sending leaves a name
	 * to ask the chain about rather than a question nobody can answer. Without it a sweep could be sent
	 * twice and bank profit the machine only earned once, leaving the float short.
	 */
	"sweep.submitted": object({
		sweepId: id,
		signature,
		lastValidBlockHeight: amount,
	}),
	/** Banked. The vault's balance is the sum of these, which is what makes it rebuildable. */
	"sweep.completed": object({
		sweepId: id,
		to: address,
		mint: address,
		amount,
		signature,
		/** What it cost to send, paid in lamports out of the machine's wallet. */
		feeLamports: amount,
		slot: amount,
	}),
	"sweep.failed": object({
		sweepId: id,
		reason: z.string().min(1).max(500),
		/** Present when it was signed and sent, and failed after that. */
		signature: signature.optional(),
	}),
};

const PAYLOADS = {
	...RUN_PAYLOADS,
	...TRADE_PAYLOADS,
	...MACHINE_PAYLOADS,
	...WITHDRAWAL_PAYLOADS,
	...SWEEP_PAYLOADS,
	...AUTHORITY_PAYLOADS,
} as const;

export type EventType = keyof typeof PAYLOADS;

/** Every event type, in the order they are defined. The database refuses any type not in this list. */
export const EVENT_TYPES = Object.keys(PAYLOADS) as readonly EventType[];

/** The payload schema for one event type, for a reader that handles a single kind. */
export const eventPayload = <T extends EventType>(type: T): (typeof PAYLOADS)[T] => PAYLOADS[type];

const GROUPS = {
	run: RUN_PAYLOADS,
	trade: TRADE_PAYLOADS,
	machine: MACHINE_PAYLOADS,
	withdrawal: WITHDRAWAL_PAYLOADS,
	sweep: SWEEP_PAYLOADS,
	authority: AUTHORITY_PAYLOADS,
} as const;

export type EventGroup = keyof typeof GROUPS;

/** The event types in one group, for a reader that only handles one kind. */
export const eventTypesOf = <G extends EventGroup>(
	group: G,
): readonly (keyof (typeof GROUPS)[G])[] =>
	Object.keys(GROUPS[group]) as (keyof (typeof GROUPS)[G])[];

/** An event as it is written to the record, with its payload typed by its event type. */
export type RecordedEvent = {
	[T in EventType]: {
		machineId: string;
		type: T;
		payload: z.infer<(typeof PAYLOADS)[T]>;
	};
}[EventType];

const envelope = z.object({ machineId: id, type: z.enum(EVENT_TYPES) });

export type ParsedEvent =
	| { ok: true; value: RecordedEvent }
	| { ok: false; error: z.ZodError<unknown> };

/**
 * Checks an event before it is written. Nothing reaches the record without passing through here: the
 * envelope first, so an unknown type is refused before any payload is looked at, then the payload
 * against that type's own schema.
 */
export function parseEvent(event: unknown): ParsedEvent {
	const outer = envelope.safeParse(event);
	if (!outer.success) return { ok: false, error: outer.error };
	const payload = PAYLOADS[outer.data.type].safeParse((event as { payload: unknown }).payload);
	if (!payload.success) return { ok: false, error: payload.error };
	return {
		ok: true,
		value: {
			machineId: outer.data.machineId,
			type: outer.data.type,
			payload: payload.data,
		} as RecordedEvent,
	};
}
